import React from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, Checkbox, CircularProgress } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Euro as EuroIcon, Person as PersonIcon, Business as BusinessIcon, Work as WorkIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, CalendarToday as CalendarTodayIcon, Email as EmailIcon } from '@mui/icons-material';

import ResponsiveTable from '../../../components/common/ResponsiveTable';
import ItemCard from '../../../components/common/ItemCard';
import { isSubscriptionService } from '../utils/serviceListingFilters';

const ServiceTable = ({ 
  services, 
  loading, 
  onEdit, 
  onDelete, 
  sortField, 
  sortDirection, 
  onSort,
  multiSelectMode = false,
  selectedServices = [],
  onServiceSelect,
  onSelectAll,
  emailWarningDays = [10, 7, 3],
  emailEnabled = true,
  onSendExpirationEmail,
  sendingExpirationEmailId = null,
  isExpirationEmailBusy = false,
}) => {
  // ==================== HELPER FUNCTIONS ====================
  
  /**
   * ✅ ΣΩΣΤΟ: Χρησιμοποιεί target_amount από backend
   * Το backend υπολογίζει σωστά: base_price + VAT - withholding
   */
  const getTargetTotal = (service) => {
    // Προτεραιότητα: backend-calculated target_amount
    if (service.target_amount !== null && service.target_amount !== undefined) {
      return parseFloat(service.target_amount);
    }
    
    // Fallback: αν δεν υπάρχει target_amount (παλιά data)
    // Επιστρέφουμε μόνο το base price
    return parseFloat(service.price || 0);
  };

  /**
   * Υπολογίζει πληροφορίες πληρωμής
   */
  const formatPaymentInfo = (service) => {
    const paid = parseFloat(service.paid_amount || 0);
    const total = getTargetTotal(service);
    const balance = total - paid;
    return {
      paid,
      total,
      balance,
      percentage: total > 0 ? Math.round((paid / total) * 100) : 0
    };
  };

  /**
   * Ελέγχει αν η υπηρεσία είναι subscription
   */
  const isSubscription = (service) => isSubscriptionService(service);

  // Helper functions for formatting (defined early for use in formatServiceTitle)
  const formatServiceType = (type) => {
    const types = {
      'development': 'Ανάπτυξη',
      'hosting': 'Hosting',
      'maintenance': 'Συντήρηση',
      'design': 'Σχεδιασμός',
      'domain': 'Domain',
      'application': 'Εφαρμογή (SaaS)',
      'support': 'Υποστήριξη',
      'seo': 'SEO',
      'consulting': 'Σύμβουλος',
      'training': 'Εκπαίδευση',
      'license': 'Άδεια Λογισμικού',
      'other': 'Άλλο'
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Δεν έχει οριστεί';
    return new Date(dateString).toLocaleDateString('el-GR');
  };

  /**
   * Μορφοποιεί τον τίτλο υπηρεσίας με prefix για subscriptions
   * Format: "Type prefix - name (start date - end date)"
   */
  const formatServiceTitle = (service) => {
    const baseName = service.name || '';
    
    // Αν δεν είναι subscription, επιστρέφουμε απλώς το όνομα
    if (!isSubscription(service)) {
      return baseName;
    }
    
    // Για subscriptions, προσθέτουμε type prefix και dates
    const typePrefix = formatServiceType(service.service_type);
    
    // Format dates only if they exist (avoid showing 'Δεν έχει οριστεί')
    let startDate = '';
    let endDate = '';
    if (service.start_date) {
      const formatted = formatDate(service.start_date);
      if (formatted && formatted !== 'Δεν έχει οριστεί') {
        startDate = formatted;
      }
    }
    if (service.end_date) {
      const formatted = formatDate(service.end_date);
      if (formatted && formatted !== 'Δεν έχει οριστεί') {
        endDate = formatted;
      }
    }
    
    // Αν υπάρχουν dates, προσθέτουμε το range
    if (startDate && endDate) {
      return `${typePrefix} - ${baseName} (${startDate}-${endDate})`;
    } else if (startDate) {
      return `${typePrefix} - ${baseName} (${startDate})`;
    }
    
    // Αν δεν υπάρχουν dates, προσθέτουμε μόνο το prefix
    return `${typePrefix} - ${baseName}`;
  };

  // Mobile card renderer
  const renderServiceCard = (service) => {
    const hasInvoice = Array.isArray(service.invoice_numbers) && service.invoice_numbers.length > 0;
    const targetAmount = getTargetTotal(service);
    const paymentInfo = formatPaymentInfo(service);
    
    // Format invoice info
    let invoiceText = '';
    if (hasInvoice && service.invoice_numbers) {
      // Calculate VAT amount using the same formula as desktop table
      // Χρησιμοποίησε το πραγματικό VAT rate από το invoice (αν υπάρχει), αλλιώς το default
      // Χρησιμοποιούμε typeof check και !== null/undefined για να επιτρέψουμε 0 (0% VAT)
      const base = parseFloat(service.price || 0);
      const vatRate = (service.invoice_vat_rate !== null && service.invoice_vat_rate !== undefined && typeof service.invoice_vat_rate === 'number')
        ? parseFloat(service.invoice_vat_rate) 
        : parseFloat(service.vat_rate || 0);
      const vatAmount = base * (vatRate / 100);
      invoiceText = `€${targetAmount.toFixed(2)} (+ ΦΠΑ: €${vatAmount.toFixed(2)})`;
    } else {
      invoiceText = 'Δεν έχει εκδοθεί τιμολόγιο';
    }
    
    return (
      <ItemCard
        key={service.id}
        title={formatServiceTitle(service)}
        subtitle={null}
        headerActions={[
          // Checkbox for multi-select mode
          ...(multiSelectMode ? [
            <Checkbox
              key="select"
              checked={selectedServices.includes(service.id)}
              onChange={() => onServiceSelect(service.id)}
              color="primary"
              size="small"
            />
          ] : []),
          <Chip 
            key="status"
            label={formatStatus(service.status)}
            color={getStatusColor(service.status)}
            size="small"
          />,
          <Chip 
            key="payment"
            label={formatPaymentStatus(service.payment_status)}
            color={getPaymentStatusColor(service.payment_status)}
            variant="outlined"
            size="small"
          />,
          <Chip 
            key="billing"
            label={isSubscription(service) ? 'Subscription' : 'One-Time'}
            color={isSubscription(service) ? 'primary' : 'secondary'}
            variant={isSubscription(service) ? 'filled' : 'outlined'}
            size="small"
          />,
          ...(isSubscription(service) && service.auto_renew ? [
            <Chip 
              key="auto-renew"
              label="Auto-Renew"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          ] : [])
        ]}
        basicInfo={[
          // Ποσό μόνο (χωρίς όνομα πελάτη)
          { 
            icon: <EuroIcon fontSize="small" />, 
            text: `€${targetAmount.toFixed(2)}`
          },
          // Όνομα πελάτη
          { 
            icon: <PersonIcon fontSize="small" />, 
            text: service.client?.name || `Άγνωστος πελάτης (ID: ${service.client_id})` 
          },
          // Project name
          { 
            icon: <WorkIcon fontSize="small" />, 
            text: service.project?.name || (service.project_id ? `Άγνωστο έργο (ID: ${service.project_id})` : 'Δεν έχει οριστεί') 
          },
          // Service type
          { 
            icon: <BusinessIcon fontSize="small" />, 
            text: formatServiceType(service.service_type) 
          },
          // Ημερομηνία δημιουργίας
          { 
            icon: <CalendarTodayIcon fontSize="small" />, 
            text: service.created_at ? `Δημιουργία: ${formatDate(service.created_at)}` : null 
          },
          // Εξοφλημένο | Υπόλοιπο
          { 
            icon: <EuroIcon fontSize="small" />, 
            text: `Εξοφλημένο: €${paymentInfo.paid.toFixed(2)}  |  Υπόλοιπο: €${paymentInfo.balance.toFixed(2)}`
          },
          // Τιμολόγιο info
          {
            icon: hasInvoice ? <CheckCircleIcon fontSize="small" /> : <WarningIcon fontSize="small" />,
            text: invoiceText
          }
        ].filter(info => info.text)}
      actions={[
        renderExpirationEmailAction(service),
        onEdit && (
          <IconButton
            key="edit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(service);
            }}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
        onDelete && (
          <IconButton
            key="delete"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(service);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )
      ].filter(Boolean)}
      />
    );
  };

  // Helper functions
  const formatStatus = (status) => {
    const statuses = {
      'active': 'Ενεργή',
      'on_hold': 'Σε Αναμονή',
      'grace_period': 'Περίοδος Χάριτος',
      'suspended': 'Αναστολή',
      'completed': 'Ολοκληρωμένη',
      'expired': 'Ληγμένη',
      'cancelled': 'Ακυρωμένη',
      'pending': 'Εκκρεμής'
    };
    return statuses[status] || status;
  };

  const formatPaymentStatus = (status) => {
    const statuses = {
      'unpaid': 'Ανεξόφλητη',
      'partial': 'Μερικώς εξοφλημένη',
      'paid': 'Εξοφλημένη'
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'on_hold': 'warning',
      'grace_period': 'warning',
      'suspended': 'error',
      'completed': 'info',
      'expired': 'error',
      'cancelled': 'default',
      'pending': 'warning'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'unpaid': 'error',
      'partial': 'warning',
      'paid': 'success'
    };
    return colors[status] || 'default';
  };

  const formatPrice = (price) => {
    return `€${parseFloat(price).toFixed(2)}`;
  };

  // Helper function to check if service is expiring soon (within 30 days)
  const isExpiringSoon = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  };

  // Helper function to check if service has expired
  const isExpired = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    const end = new Date(endDate);
    return end < today;
  };

  // Helper function to get days until expiration
  const getDaysUntilExpiration = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const maxWarningDays = Math.max(...(emailWarningDays || [10, 7, 3]), 0);

  const isEligibleForExpirationEmail = (service) => {
    if (!emailEnabled) return false;
    if (!isSubscription(service)) return false;
    if (service.status !== 'active') return false;
    const daysLeft = getDaysUntilExpiration(service.end_date);
    return daysLeft !== null && daysLeft > 0 && daysLeft <= maxWarningDays;
  };

  const renderExpirationEmailAction = (service) => {
    if (!onSendExpirationEmail || !isEligibleForExpirationEmail(service)) return null;
    const isSending = sendingExpirationEmailId === service.id;
    const isDisabled = isExpirationEmailBusy;
    return (
      <Tooltip title={isSending ? 'Αποστολή email...' : 'Αποστολή email υπενθύμισης λήξης'}>
        <span>
          <IconButton
            size="small"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) {
                onSendExpirationEmail(service);
              }
            }}
            sx={{ color: isDisabled ? 'action.disabled' : 'info.main' }}
          >
            {isSending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <EmailIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // Table columns configuration
  const columns = [
    // Checkbox column for multi-select mode
    ...(multiSelectMode ? [{
      field: 'select',
      headerName: (
        <Checkbox
          checked={selectedServices.length === services.length && services.length > 0}
          indeterminate={selectedServices.length > 0 && selectedServices.length < services.length}
          onChange={onSelectAll}
          color="primary"
        />
      ),
      sortable: false,
      minWidth: 50,
      renderCell: (value, row) => (
        <Checkbox
          checked={selectedServices.includes(row.id)}
          onChange={() => onServiceSelect(row.id)}
          color="primary"
        />
      )
    }] : []),
    {
      field: 'name',
      headerName: 'Όνομα Υπηρεσίας',
      sortable: true,
      minWidth: 200,
      renderCell: (value, row) => (
        <Typography variant="body2" fontWeight={500}>
          {formatServiceTitle(row)}
        </Typography>
      )
    },
    {
      field: 'client',
      headerName: 'Πελάτης',
      sortable: true,
      sortField: 'client_name',
      minWidth: 150,
      renderCell: (value, row) => (
        <Typography variant="body2">
          {value?.name || `Άγνωστος πελάτης (ID: ${row.client_id})`}
        </Typography>
      )
    },
    {
      field: 'project',
      headerName: 'Έργο',
      sortable: true,
      sortField: 'project_name',
      minWidth: 150,
      renderCell: (value, row) => (
        <Typography variant="body2">
          {value?.name || (row.project_id ? `Άγνωστο έργο (ID: ${row.project_id})` : 'Δεν έχει οριστεί')}
        </Typography>
      )
    },
    {
      field: 'service_type',
      headerName: 'Τύπος',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={formatServiceType(value)}
          color="info"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Ημ. Δημιουργίας',
      sortable: true,
      sortField: 'created_at',
      minWidth: 120,
      renderCell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(value)}
        </Typography>
      )
    },
    {
      field: 'billing_type',
      headerName: 'Χρέωση',
      sortable: true,
      minWidth: 100,
      renderCell: (value, row) => {
        const isSub = isSubscription(row);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Chip
              label={isSub ? 'Subscription' : 'One-Time'}
              color={isSub ? 'primary' : 'secondary'}
              variant={isSub ? 'filled' : 'outlined'}
              size="small"
            />
            {isSub && row.auto_renew && (
              <Chip
                label="Auto-Renew"
                color="success"
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'price',
      headerName: 'Τιμή',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => {
        const base = parseFloat(value || 0);
        const hasInvoice = Array.isArray(row.invoice_numbers) && row.invoice_numbers.length > 0;
        const targetAmount = getTargetTotal(row);  // ΠΛΗΡΩΤΕΟ
        const withholdingTax = parseFloat(row.withholding_tax || 0);
        const hasWithholding = withholdingTax > 0;
        // Χρησιμοποίησε το πραγματικό VAT rate από το invoice (αν υπάρχει), αλλιώς το default
        // Χρησιμοποιούμε typeof check και !== null/undefined για να επιτρέψουμε 0 (0% VAT)
        const vatRate = (row.invoice_vat_rate !== null && row.invoice_vat_rate !== undefined && typeof row.invoice_vat_rate === 'number')
          ? parseFloat(row.invoice_vat_rate) 
          : parseFloat(row.vat_rate || 0);
        
        const vatAmount = base * (vatRate / 100);

        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={600}>{formatPrice(base)}</Typography>
        {hasInvoice && (
          <>
            <Typography variant="caption" color="text.secondary">
              ΦΠΑ: {formatPrice(vatAmount)}
            </Typography>
            {hasWithholding && (
              <Typography variant="caption" color="warning.main">
                Παρακράτηση: {formatPrice(withholdingTax)}
              </Typography>
            )}
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Πληρωτέο: {formatPrice(targetAmount)}
            </Typography>
          </>
        )}
      </Box>
        );
      }
    },
    {
      field: 'invoice_numbers',
      headerName: 'Τιμολόγια',
      sortable: false,
      minWidth: 150,
      renderCell: (value, row) => {
        // value might be the invoice_numbers array or object, always use row
        const invoiceNumbers = Array.isArray(row.invoice_numbers) 
          ? row.invoice_numbers 
          : (typeof value === 'object' && value !== null && Array.isArray(value) ? value : []);
        
        if (invoiceNumbers.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Δεν έχει εκδοθεί
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {invoiceNumbers.map((invoiceNumber, index) => (
              <Chip
                key={index}
                label={String(invoiceNumber || '')}
                color="success"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        );
      }
    },
    {
      field: 'payment_info',
      headerName: 'Πληρωμές',
      sortable: true,
      sortField: 'balance',
      minWidth: 140,
      renderCell: (value, row) => {
        // Ignore value parameter, always use row data
        try {
          const paymentInfo = formatPaymentInfo(row);
          const hasBalance = paymentInfo && paymentInfo.balance > 0;
          if (!paymentInfo || typeof paymentInfo !== 'object') {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Εξοφλημένο: €{(paymentInfo.paid || 0).toFixed(2)}
              </Typography>
              <Typography 
                variant="caption" 
                color={hasBalance ? 'error.main' : 'text.secondary'}
                sx={{ fontWeight: hasBalance ? 'bold' : 'normal' }}
              >
                Υπόλοιπο: €{(paymentInfo.balance || 0).toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {(paymentInfo.percentage || 0)}%
                </Typography>
                <Box sx={{ 
                  width: 40, 
                  height: 4, 
                  backgroundColor: 'grey.300', 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    width: `${paymentInfo.percentage || 0}%`, 
                    height: '100%', 
                    backgroundColor: (paymentInfo.percentage || 0) === 100 ? 'success.main' : 
                                   (paymentInfo.percentage || 0) > 0 ? 'warning.main' : 'error.main'
                  }} />
                </Box>
              </Box>
            </Box>
          );
        } catch (error) {
          console.error('Error rendering payment_info:', error);
          return (
            <Typography variant="body2" color="error">
              Error
            </Typography>
          );
        }
      }
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={formatStatus(value)}
          color={getStatusColor(value)}
          size="small"
        />
      )
    },
    {
      field: 'payment_status',
      headerName: 'Πληρωμή',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={formatPaymentStatus(value)}
          color={getPaymentStatusColor(value)}
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'end_date',
      headerName: 'Λήξη',
      sortable: true,
      minWidth: 140,
      renderCell: (value, row) => {
        if (!value) {
          return (
            <Typography variant="body2" color="text.secondary">
              Δεν έχει οριστεί
            </Typography>
          );
        }

        const daysLeft = getDaysUntilExpiration(value);
        const isExpiring = isExpiringSoon(value);
        const hasExpired = isExpired(value);
        const isSub = isSubscription(row);

        if (!isSub) {
          return (
            <Typography variant="body2" color="text.secondary">
              One-Time
            </Typography>
          );
        }

        if (hasExpired) {
          const daysExpired = Math.abs(daysLeft);
          return (
            <Tooltip title={`Έληξε πριν ${daysExpired} ημέρες`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon color="error" fontSize="small" />
                <Typography variant="body2" color="error.main" fontWeight={500}>
                  {formatDate(value)}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        if (isExpiring) {
          return (
            <Tooltip title={`Λήγει σε ${daysLeft} ημέρες`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon color="warning" fontSize="small" />
                <Typography variant="body2" color="warning.main" fontWeight={500}>
                  {formatDate(value)}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">
              {formatDate(value)}
            </Typography>
          </Box>
        );
      }
    },
    ...(onEdit || onDelete || onSendExpirationEmail ? [{
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 120,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {renderExpirationEmailAction(row)}
          {onEdit && (
            <IconButton
              size="small"
              onClick={() => onEdit(row)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={() => onDelete(row)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    }] : [])
  ];

  return (
    <ResponsiveTable
      data={services}
      columns={columns}
      loading={loading}
      emptyMessage="Δεν βρέθηκαν υπηρεσίες"
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={onSort}
      renderMobileCard={renderServiceCard}
    />
  );
};

export default ServiceTable;